import type { CurrencyCode } from '@/shared/lib/currency';
import { getCurrencyDecimals } from '@/shared/lib/currency';
import { getMultiplierTierClass } from '@/shared/lib/game/getMultiplierTierClass';

import type { Listener } from '../../model/observable';

const HEARTBEAT_THRESHOLD = 5;

/**
 * Paints the live multiplier value + tier/heartbeat classes onto `node`.
 * Tier/heartbeat are diffed so class writes only happen on actual changes,
 * not every frame; the heartbeat pulse itself runs as a CSS animation.
 */
export function createMultiplierPainter(
  node: HTMLElement,
  isFlying: () => boolean,
  isCrashed: () => boolean,
): Listener<number> {
  let lastTier = '';
  let lastHeartbeat = false;

  return (value) => {
    node.textContent = `${value.toFixed(2)}x`;
    if (isCrashed()) return;

    const tier = getMultiplierTierClass(value);
    const heartbeat = isFlying() && value >= HEARTBEAT_THRESHOLD;
    if (tier !== lastTier || heartbeat !== lastHeartbeat) {
      lastTier = tier;
      lastHeartbeat = heartbeat;
      node.className = `jmd__value ${tier}${heartbeat ? ' jmd__value--heartbeat' : ''}`;
    }
  };
}

/** Paints the live cash-out payout (amount × multiplier) onto `node`. */
export function createPayoutPainter(
  node: HTMLElement,
  amount: number,
  currency: CurrencyCode,
): Listener<number> {
  const decimals = getCurrencyDecimals(currency);
  return (value) => {
    const livePayout = Math.floor(amount * value * 100) / 100;
    node.textContent = `Cash Out ${livePayout.toFixed(decimals)} ${currency}`;
  };
}
